import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { buildApiUrl } from '../utils/api';

const AdventureDetailPage = () => {
  const { slug } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [adventure, setAdventure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAdventureDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(buildApiUrl(`/adventures-pro/${slug}`));

        if (response.ok) {
          const data = await response.json();
          setAdventure(data);
        } else {
          setError('Adventure not found');
        }
      } catch (err) {
        console.error('Error fetching adventure:', err);
        setError('Failed to load adventure details');
      } finally {
        setLoading(false);
      }
    };

    fetchAdventureDetails();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading adventure details...</p>
        </div>
      </div>
    );
  }

  if (error || !adventure) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <Link
            to="/adventures"
            className="bg-ocean-600 text-white px-6 py-2 rounded-lg hover:bg-ocean-700 transition-colors"
          >
            Back to Adventures
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📋' },
    { id: 'included', label: 'What\'s Included', icon: '✅' },
    { id: 'itinerary', label: 'Itinerary', icon: '📅' },
    { id: 'details', label: 'Details', icon: 'ℹ️' }
  ];

  // Map database fields to display format
  const displayAdventure = {
    ...adventure,
    image: adventure.hero_image,
    price: adventure.price_display,
    groupSize: `${adventure.group_size_min}-${adventure.group_size_max} people max`,
    skillLevel: adventure.skill_level ? adventure.skill_level.charAt(0).toUpperCase() + adventure.skill_level.slice(1) : 'Beginner',
    description: adventure.short_description,
    detailedDescription: adventure.full_description,
    prerequisites: adventure.prerequisites,
    included: adventure.included_items || [],
    itinerary: (adventure.itinerary || []).map(item => `${item.title}: ${item.description}`),
    bestFor: adventure.highlights || [],
    tags: adventure.tags || []
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-20 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative h-96 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover"
          style={{ 
            backgroundImage: `url(${displayAdventure.image})`,
            backgroundPosition: adventure.hero_image_position || 'center'
          }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative z-10 h-full flex items-center justify-center text-center">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">{displayAdventure.title}</h1>
              <p className="text-2xl text-coral-400 font-semibold mb-2">{displayAdventure.price}</p>
              <p className="text-xl text-white/90">{displayAdventure.duration} • {displayAdventure.groupSize}</p>
              <div className="flex justify-center gap-4 mt-6">
                {displayAdventure.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Navigation Tabs */}
            <div className="flex flex-wrap gap-2 mb-8 bg-white p-2 rounded-2xl shadow-lg">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-0 px-4 py-3 rounded-xl font-semibold transition-all ${
                    activeTab === tab.id
                      ? 'bg-coral-500 text-white shadow-lg transform scale-105'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="glass-card-gradient p-4 sm:p-6 rounded-2xl"
            >
              {activeTab === 'overview' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Experience Overview</h2>
                  <p className="text-base text-white/90 leading-relaxed mb-6">{displayAdventure.detailedDescription}</p>
                  
                  {/* Prerequisites Section */}
                  {displayAdventure.prerequisites && (
                    <div className="mb-6 p-4 bg-amber-500/20 border border-amber-400/40 rounded-xl">
                      <h3 className="text-lg font-semibold text-amber-300 mb-3 flex items-center">
                        <span className="mr-2">⚠️</span>
                        Important Information
                      </h3>
                      <div className="text-white/90 whitespace-pre-line text-sm leading-relaxed">
                        {displayAdventure.prerequisites.split('**').map((part, index) => 
                          index % 2 === 1 ? <strong key={index}>{part}</strong> : part
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-6">
                    <div className="bg-coral-50 p-3 sm:p-4 rounded-xl text-center">
                      <div className="text-2xl mb-1">⏱️</div>
                      <div className="font-semibold text-coral-800 text-sm">Duration</div>
                      <div className="text-coral-600 text-sm">{displayAdventure.duration}</div>
                    </div>
                    <div className="bg-ocean-50 p-3 sm:p-4 rounded-xl text-center">
                      <div className="text-2xl mb-1">👥</div>
                      <div className="font-semibold text-ocean-800 text-sm">Group Size</div>
                      <div className="text-ocean-600 text-sm">{displayAdventure.groupSize}</div>
                    </div>
                    <div className="bg-green-50 p-3 sm:p-4 rounded-xl text-center">
                      <div className="text-2xl mb-1">🎯</div>
                      <div className="font-semibold text-green-800 text-sm">Skill Level</div>
                      <div className="text-green-600 text-sm">{displayAdventure.skillLevel}</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Perfect For</h3>
                    <div className="flex flex-wrap gap-2">
                      {displayAdventure.bestFor.map((item, idx) => (
                        <div
                          key={idx}
                          className="bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg text-sm font-medium"
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'included' && (
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">What&apos;s Included</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {displayAdventure.included.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-6 h-6 bg-coral-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                          <span className="text-white text-sm">✓</span>
                        </div>
                        <span className="text-white/90">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'itinerary' && (
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">Detailed Itinerary</h2>
                  <div className="space-y-6">
                    {displayAdventure.itinerary.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-10 h-10 bg-ocean-500 rounded-full flex items-center justify-center text-white font-bold mr-4 mt-1 flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="text-white/90 leading-relaxed">{item}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'details' && (
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">Important Details</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-3">Meeting Location</h3>
                      <p className="text-white/90 bg-white/20 p-4 rounded-xl backdrop-blur-sm">{displayAdventure.location}</p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-white mb-3">What to Bring</h3>
                      <div className="bg-white/20 p-6 rounded-xl backdrop-blur-sm">
                        <ul className="space-y-2 text-white/90">
                          <li>• Swimwear and towel</li>
                          <li>• Sun protection (hat, sunscreen, sunglasses)</li>
                          <li>• Comfortable clothes for before/after</li>
                          <li>• Water bottle</li>
                          <li>• Camera (waterproof case recommended)</li>
                          <li>• Light snacks (if desired)</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-white mb-3">Health & Safety</h3>
                      <div className="bg-white/20 p-6 rounded-xl backdrop-blur-sm">
                        <ul className="space-y-2 text-white/90">
                          <li>• Medical questionnaire required</li>
                          <li>• Basic swimming ability necessary</li>
                          <li>• Age restrictions may apply</li>
                          <li>• Full safety briefing provided</li>
                          <li>• Professional first aid certified instructor</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-white mb-3">Cancellation Policy</h3>
                      <div className="bg-white/20 p-6 rounded-xl backdrop-blur-sm">
                        <p className="text-white/90">
                          • 7+ days: Full refund<br/>
                          • 3-6 days: 50% refund<br/>
                          • 0-2 days: No refund<br/>
                          • Weather cancellations: Full refund or reschedule
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Booking Card - Only sticky on large screens */}
            <div className="bg-white p-6 rounded-3xl lg:sticky lg:top-32 shadow-lg border border-slate-200">
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-slate-800 mb-2">{adventure.price}</div>
                <div className="text-slate-600">per person</div>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-slate-600">Duration:</span>
                  <span className="font-semibold text-slate-800">{displayAdventure.duration}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Group Size:</span>
                  <span className="font-semibold text-slate-800">{displayAdventure.groupSize}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Location:</span>
                  <span className="font-semibold text-slate-800 text-right text-sm">{displayAdventure.location}</span>
                </div>
              </div>

              <div className="space-y-3">
                <Link
                  to="/book"
                  className="w-full bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white py-4 rounded-xl font-semibold text-center transition-all transform hover:scale-105 shadow-lg block"
                >
                  Book This Adventure
                </Link>
                <Link
                  to="/contact"
                  className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 py-3 rounded-xl font-semibold text-center transition-all block"
                >
                  Ask Questions
                </Link>
              </div>

              <div className="mt-6 text-center">
                <div className="text-sm text-slate-500 mb-2">Have questions?</div>
                <div className="flex justify-center space-x-4">
                  <a
                    href="https://wa.me/61414710541"
                    className="text-green-600 hover:text-green-700 font-semibold text-sm"
                  >
                    💬 WhatsApp
                  </a>
                  <a
                    href="mailto:ahmed@divecampadventures.com"
                    className="text-coral-600 hover:text-coral-700 font-semibold text-sm"
                  >
                    📧 Email
                  </a>
                </div>
              </div>
            </div>

            {/* Safety Guarantee */}
            <div className="bg-green-50 p-6 rounded-3xl border border-green-200">
              <div className="text-center">
                <div className="text-4xl mb-3">🛡️</div>
                <h3 className="text-lg font-semibold text-green-800 mb-2">Safety Guarantee</h3>
                <p className="text-green-700 text-sm">
                  PADI MSDT certified instructor with 8+ years experience. 
                  Full insurance coverage and emergency protocols in place.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Adventures section removed - can be re-added later */}
      </div>
    </div>
  );
};

export default AdventureDetailPage;